import {useContext, useState} from 'react';
import dayjs from 'dayjs';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {ExchangeRate, useExchangeRates, useFunds} from '../hooks/useDomainData';
import {ExchangeRateEditor} from '../components/FormikStoreEditors/ExchangeRateEditor';
import {useAppSettings} from '../components/AppSettings';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';

function ExchangeRates() {
  const user = useContext(UserContext);
  const person = useAuthPersonInfo();
  const exchangeRates = useExchangeRates();
  const funds = useFunds();
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    ExchangeRate | undefined
  >();

  const collectionName = 'exchangeRates';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: ExchangeRate) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: ExchangeRate) => {
    console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Exchange Rates</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Exchange Rate
          </Button>
        )}
      </View>

      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!exchangeRates && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {exchangeRates && (
            <DataTable.Header>
              <DataTable.Title>Start</DataTable.Title>
              <DataTable.Title>End</DataTable.Title>
              <DataTable.Title>Origin</DataTable.Title>
              <DataTable.Title>Destination</DataTable.Title>
              <DataTable.Title>Rate</DataTable.Title>
              {(person?.isAdmin || person?.isGuestRelations) && (
                <DataTable.Title>Actions</DataTable.Title>
              )}
            </DataTable.Header>
          )}
          {exchangeRates && exchangeRates.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No exchange rates found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {exchangeRates &&
            exchangeRates.length > 0 &&
            exchangeRates.map(exchangeRate => (
              <DataTable.Row key={exchangeRate.exchangeRateId}>
                <DataTable.Cell>
                  {dayjs(exchangeRate.dateRangeStartDate).format('MM/DD/YYYY')}
                </DataTable.Cell>
                <DataTable.Cell>
                  {dayjs(exchangeRate.dateRangeEndDate).format('MM/DD/YYYY')}
                </DataTable.Cell>
                <DataTable.Cell>
                  {exchangeRate.originFund?.fundCode}
                </DataTable.Cell>
                <DataTable.Cell>
                  {exchangeRate.destinationFund?.fundCode}
                </DataTable.Cell>
                <DataTable.Cell>{exchangeRate.exchangeRate}</DataTable.Cell>
                {(person?.isAdmin || person?.isGuestRelations) && (
                  <DataTable.Cell>
                    <>
                      {(person?.isAdmin || person?.isGuestRelations) && (
                        <IconButton
                          icon="pencil"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleEditFirestoreObject(exchangeRate)
                          }
                        />
                      )}
                      {person?.isAdmin && (
                        <IconButton
                          icon="delete"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleDeleteFirestoreObject.mutate(exchangeRate)
                          }
                        />
                      )}
                    </>
                  </DataTable.Cell>
                )}
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>

      <ExchangeRateEditor
        exchangeRates={exchangeRates ?? []}
        funds={funds ?? []}
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        exchangeRate={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default ExchangeRates;
